import type { SupabaseClient } from '../client'
import type { Database } from '../database.types'
import { SupabaseError } from '../errors'
import { unwrapList, unwrapSingle } from './_helpers'

export type BugReportRow = Database['portal']['Tables']['bug_reports']['Row']
export type BugReportInsert = Database['portal']['Tables']['bug_reports']['Insert']

// ADR-084: desfecho, task_id e a nota da triagem entram aqui porque a lista do
// portal mostra o estado do chamado, e o estado vem do que a triagem decidiu.
const BUG_COLUMNS =
  'id, project_id, initiative_id, reported_by, title, description, expected_behavior, severity, status, screenshots, browser_info, page_url, resolved_at, resolved_by, resolution_note, executor, executor_id, desfecho, desfecho_nota, triado_at, task_id, created_at, updated_at'

const SCREENSHOTS_BUCKET = 'bug-screenshots'

export type BugReportFilter = {
  initiativeId?: number | null
}

/**
 * Lista bug reports de um projeto, mais recentes primeiro.
 * Aceita filtro opcional por iniciativa.
 */
export async function listBugReportsByProject(
  client: SupabaseClient,
  projectId: number,
  opts: BugReportFilter = {},
): Promise<BugReportRow[]> {
  let query = client
    .schema('portal')
    .from('bug_reports')
    .select(BUG_COLUMNS)
    .eq('project_id', projectId)
    .order('created_at', { ascending: false })

  if (typeof opts.initiativeId === 'number') {
    query = query.eq('initiative_id', opts.initiativeId)
  } else if (opts.initiativeId === null) {
    query = query.is('initiative_id', null)
  }

  const relatos = unwrapList(await query) as unknown as BugReportRow[]
  if (relatos.length === 0) return relatos

  // ADR-084: o estado que o cliente lê é DERIVADO (desfecho da triagem + status
  // da tarefa) e mora em portal.v_relato_estado. Buscar aqui, e não recalcular
  // no navegador, é o que impede a tela e o banco de discordarem.
  const { data: estados } = await client
    .schema('portal')
    .from('v_relato_estado')
    .select('bug_report_id, estado, task_status')
    .in(
      'bug_report_id',
      relatos.map((r) => r.id),
    )

  const porId = new Map(
    (
      (estados ?? []) as { bug_report_id: number; estado: string; task_status: string | null }[]
    ).map((e) => [e.bug_report_id, e]),
  )

  return relatos.map((r) => ({
    ...r,
    estado: porId.get(r.id)?.estado ?? null,
    task_status: porId.get(r.id)?.task_status ?? null,
  })) as unknown as BugReportRow[]
}

/**
 * Faz upload de um arquivo no bucket de screenshots e retorna o path interno.
 */
async function uploadScreenshot(
  client: SupabaseClient,
  projectId: number,
  file: File,
): Promise<string> {
  const ext = file.name.split('.').pop() ?? 'bin'
  const path = `${projectId}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`
  const { error } = await client.storage.from(SCREENSHOTS_BUCKET).upload(path, file)
  if (error) throw new SupabaseError(error.message, error)
  return path
}

export type CreateBugReportInput = {
  projectId: number
  reportedBy: string
  title: string
  description: string
  expectedBehavior?: string | null
  severity: string
  initiativeId?: number | null
  screenshotFiles?: File[]
  /** Quem resolve: `infosaas` (o padrão), `cliente` ou `terceiro`. */
  executor?: 'infosaas' | 'cliente' | 'terceiro'
  /** O fornecedor, obrigatório quando `executor` é `terceiro`. */
  executorId?: string | null
}

/**
 * Cria um bug report fazendo upload das screenshots antes.
 * Retorna a linha criada (sem o efeito colateral do ClickUp).
 */
export async function createBugReport(
  client: SupabaseClient,
  input: CreateBugReportInput,
): Promise<BugReportRow> {
  const paths: string[] = []
  for (const file of input.screenshotFiles ?? []) {
    paths.push(await uploadScreenshot(client, input.projectId, file))
  }

  // expected_behavior foi adicionado em 20260521 mas a regeracao dos types
  // do banco ainda nao rodou; cast pra Insert pra nao quebrar TS.
  const payload = {
    project_id: input.projectId,
    initiative_id: input.initiativeId ?? null,
    reported_by: input.reportedBy,
    title: input.title,
    description: input.description,
    expected_behavior: input.expectedBehavior ?? null,
    severity: input.severity,
    screenshots: paths,
    executor: input.executor ?? 'infosaas',
    // o CHECK do banco recusa fornecedor apontado fora de `terceiro`, então a
    // limpeza acontece aqui e não depende de quem chama lembrar disso
    executor_id: input.executor === 'terceiro' ? (input.executorId ?? null) : null,
  } as BugReportInsert

  const result = await client
    .schema('portal')
    .from('bug_reports')
    .insert(payload)
    .select(BUG_COLUMNS)
    .single()

  return unwrapSingle(result, 'BugReport') as unknown as BugReportRow
}

/**
 * ADR-096 · uma troca de iniciativa feita pelo cliente.
 *
 * Os nomes e o título são os da época: quem só acompanhava a origem não lê
 * mais o relato nem a iniciativa nova, e o rastro precisa dizer para onde foi.
 */
export type RelatoMovimento = {
  id: number
  bug_report_id: number
  project_id: number
  de_initiative_id: number
  de_nome: string
  para_initiative_id: number
  para_nome: string
  titulo: string
  movido_por: string | null
  movido_por_nome: string | null
  created_at: string
}

const MOVIMENTO_COLUMNS =
  'id, bug_report_id, project_id, de_initiative_id, de_nome, para_initiative_id, para_nome, titulo, movido_por, movido_por_nome, created_at'

// a tabela e a RPC nasceram depois da última regeração dos types: o cast é o
// mesmo remendo do expected_behavior, até o `supabase gen types` rodar de novo
// biome-ignore lint/suspicious/noExplicitAny: tabela e RPC fora dos types gerados
const portalSemTipos = (client: SupabaseClient) => client.schema('portal') as any

/**
 * O cliente move o próprio relato para outra iniciativa, enquanto a equipe não
 * triou. A regra inteira mora na RPC: tester nas duas iniciativas, relato em
 * triagem, destino do mesmo projeto e não arquivado.
 */
export async function moverRelatoDeIniciativa(
  client: SupabaseClient,
  bugReportId: number,
  paraInitiativeId: number,
): Promise<{ bug_report_id: number; de_initiative_id: number; para_initiative_id: number }> {
  const { data, error } = await portalSemTipos(client).rpc('mover_relato_de_iniciativa', {
    p_bug_report_id: bugReportId,
    p_para_initiative_id: paraInitiativeId,
  })
  if (error) throw new SupabaseError(error.message, error)
  const linha = Array.isArray(data) ? data[0] : data
  return linha
}

/** As trocas de um relato, da mais antiga para a mais nova (a linha do tempo). */
export async function listMovimentosDoRelato(
  client: SupabaseClient,
  bugReportId: number,
): Promise<RelatoMovimento[]> {
  const { data, error } = await portalSemTipos(client)
    .from('relato_movimentos')
    .select(MOVIMENTO_COLUMNS)
    .eq('bug_report_id', bugReportId)
    .order('created_at', { ascending: true })
  if (error) throw new SupabaseError(error.message, error)
  return (data ?? []) as RelatoMovimento[]
}

/**
 * Os relatos que saíram desta iniciativa desde uma data: é o rastro que fica na
 * lista antiga no lugar do chamado que mudou de lugar.
 */
export async function listRastrosDaIniciativa(
  client: SupabaseClient,
  initiativeId: number,
  desde: string,
): Promise<RelatoMovimento[]> {
  const { data, error } = await portalSemTipos(client)
    .from('relato_movimentos')
    .select(MOVIMENTO_COLUMNS)
    .eq('de_initiative_id', initiativeId)
    .gte('created_at', desde)
    .order('created_at', { ascending: false })
  if (error) throw new SupabaseError(error.message, error)
  return (data ?? []) as RelatoMovimento[]
}

/**
 * ADR-100 · a recusa da RPC quando a equipe já mexeu no relato. A tela
 * reconhece o código para trocar a pergunta de apagar pela oferta de arquivar.
 */
export const RECUSA_EQUIPE_TOCOU = 'IS100'

/** `true` quando o erro é a recusa "a equipe já mexeu neste chamado". */
export function equipeJaTocou(erro: unknown): boolean {
  const causa = (erro as { cause?: { code?: string } } | null)?.cause
  const codigo = causa?.code ?? (erro as { code?: string } | null)?.code
  return codigo === RECUSA_EQUIPE_TOCOU
}

/**
 * ADR-100 · o cliente apaga o relato que abriu por engano.
 *
 * A regra inteira mora na RPC: só o autor, e só enquanto a equipe não tocou.
 * Ela devolve os caminhos dos prints e dos anexos que ele mandou, e é daqui que
 * eles saem do bucket, porque o banco não apaga arquivo (o Supabase trava o
 * DELETE em `storage.objects` por SQL). A policy do bucket só deixa o cliente
 * remover arquivo que ele mesmo enviou e que nenhum relato usa mais.
 *
 * Falhar ao remover o arquivo não desfaz o apagar: o relato já sumiu, e um
 * print órfão no bucket é sujeira, não informação errada na tela.
 */
export async function apagarRelato(
  client: SupabaseClient,
  id: number,
): Promise<{ bug_report_id: number; arquivos: string[] }> {
  const { data, error } = await portalSemTipos(client).rpc('apagar_relato', { p_id: id })
  if (error) throw new SupabaseError(error.message, error)
  const linha = (Array.isArray(data) ? data[0] : data) ?? { bug_report_id: id, arquivos: [] }
  const arquivos: string[] = linha.arquivos ?? []
  if (arquivos.length > 0) {
    await client.storage.from(SCREENSHOTS_BUCKET).remove(arquivos)
  }
  return { bug_report_id: Number(linha.bug_report_id), arquivos }
}

/**
 * O apagar do módulo legado (`modules/testing/`). Passa pela mesma RPC: o
 * DELETE direto deixou de existir no ADR-100, e manter dois caminhos seria
 * manter duas regras.
 */
export async function deleteBugReport(client: SupabaseClient, id: number): Promise<void> {
  await apagarRelato(client, id)
}

/**
 * URL pública para uma screenshot. Aceita path do storage ou URL completa.
 */
export function getBugScreenshotUrl(client: SupabaseClient, path: string | null): string | null {
  if (!path) return null
  if (path.startsWith('http')) return path
  return client.storage.from(SCREENSHOTS_BUCKET).getPublicUrl(path).data.publicUrl
}
